/** @module Specification/PartMaterialColor/AggregatePMCColorController */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi/action/FormData",
	"csi/action/New",
	"csi/config",
	"csi/def/ViewRef",
	"csi/heading/_AggregateWithNewMixin",
	"csi/heading/AggregateController",
	"csi/locale",
	"csi/store/Request",
	"csi/wizard/ActionControllerMixin",
	"csi/xml",
	"csi/xml/Query",
	"Apparel/NewColorMaterialFromMaterial",
	"Apparel/ProductType",
	"Apparel/ProductColorMixin"
], function(declare, lang, _, FormData, NewAction, config, ViewRef, _AggregateWithNewMixin, AggregateController, locale, Request, ActionControllerMixin, xml, Query, NewColorMaterialFromMaterial, ProductType, ProductColorMixin) {

	return declare([AggregateController, ActionControllerMixin, _AggregateWithNewMixin, ProductColorMixin], {

		// _AggregateWithNewMixin interface
		aggregateNewNodeType: "ColorMaterial",

		/**
		 * Allow custom error handling for New action
		 * @param {Controller} ac
		 * @param {Exception} err
		 */
		onNewError: function(ac, err) {
			if (err.csiErrorException && err.csiErrorException.includes("No edit access")) {
				ac.m_error = locale.resource("AggregateCommonColorEditAccess_Error");
				ac._errorCode = "";
			}
		},

		/**
		 * @override
		 * Prevent adding new nodes to special materials or materials whose type does not allow it
		 */
		_getNewAction: function() {
			var material = this._getProduct();
			if (material?.Special || !ProductType.get(material, "AllowCreateColoredMaterialOnPlacement")) {
				return null;
			}

			return lang.mixin(new NewAction(), {
				m_id: "New" + this.aggregateNewNodeType,
				m_typeNode: this.aggregateNewNodeType,
				classController: NewColorMaterialFromMaterial,
				m_request: lang.mixin(new Request(), {
					m_module: "Apparel",
					m_operation: "NewColorMaterialWithDefault3DMaterial"
				})
			});
		},

		/**
		 * Set context for new ColorMaterial so it is added to Material's ProductColors relationship
		 */
		_getNewControllerParams: function() {
			var params = this.inherited(arguments);
			params.m_urlParent = this._getProduct().$URL;
			params.m_idAttrParent = "ProductColors";
			return params;
		},

		/**
		 * @override
		 * Add query filters to the views in wizard
		 */
		getWizardViewQuery: function(wizard, view, index) {
			var q = new Query();
			if (index == 0) {
				q.Attr("Active", xml.OP_EQUALS, "true");
				this._applySeasonAvailabilityFilters(null, q);
			} else if (index == 1) {
				this._applyProducColorSpecFilters(null, q);
			}

			return q;
		},

		/**
		 * @override
		 * Prepare the data and pass it into the next page
		 */
		onNextPage: function(wizard, currentPage, nextPage, bFinish) {
			if (bFinish) {
				// if finished, track form data to use for writing operations
				this.m_formData = currentPage.getPageData();
				return null;
			} else if (nextPage && nextPage.getPageData()) {
				// nothing to do if form data already exists for page
				return null;
			}

			var nPage = wizard.getIndexOfPage(nextPage);
			var dfd;
			if (nPage == 0 && this._isDisableProductColors() && this._isOKColorSpecs()) {
				//show only Color Specs view
				return wizard.skipToNextPage(nextPage);
			}

			// create form data for each page - setNode to wizard view node so that referenced views are found on PSPlacement
			var fd = new FormData(this);
			fd.setNode(wizard.getViewNode());
			fd.m_bSelectOne = true;

			var mat = this.m_formData.getSelectedNode(true);
			if (mat) {
				fd.addAggregatedNode(mat);
			}

			dfd = wizard.setFormDataToPage(nextPage, fd, false);
			return dfd;
		},

		/**
		 * @override
		 * Return event object containing reference to popup view
		 */
		getActionView: function(vr) {
			var pm = this._getBOMLineItem();
			if (pm && !pm.ForStyle) {
				vr = new ViewRef();
				vr.m_id = this._isColorRuleEnforcedOrEnabled() ? "SelectColorWizardWithColorPalette" : "SelectColorWizard";
				vr.m_url = this._getProduct().$URL;

				return this.inherited(arguments, [vr]);
			}
			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Add property to the viewParams
		 */
		onWizardViewPreCreate: function(wizard, page, defView, viewParams) {
			var index = wizard.getIndexOfPage(page);
			if (index == 1) {
				const styleNode = this._getStyle();
				if (styleNode) {
					viewParams.productUrl = styleNode.$URL;
				} else {
					const productActual = this._getProduct();
					viewParams.productUrl = productActual && productActual.$URL;
				}
			}
		},

		/**
		 * @override
		 * Disable Previous button on first page
		 */
		disablePrev: function(wizard, page, index) {
			return !index || this._isDisableProductColors();
		},

		/**
		 * @override
		 * Disable next button if ColorSpecs is not enable, i.e. PartMaterial.OKColorSpecs not exist or is false
		 */
		disableNext: function(wizard, page, index, bLast) {
			if (index == 0) {
				return !this._isOKColorSpecs();
			}

			return this.inherited(arguments);
		},

		/**
		 * Get BomLineItem node
		 */
		_getBOMLineItem: function() {
			var node = this.getNode();
			// This is to handle CommonColor on BomLineItem
			if (node && (node.$Type == "PartMaterial" || node.$Type == "FoodBOMLineItem")) {
				return node;
			}
			// This is to handle PMCColor on PartMaterialColor
			return this.getCache().getNode(node.__Parent__);
		},

		/**
		 * Get the actual material on BomLineItem
		 */
		_getProduct: function() {
			return this.getCache().getNode(this._getBOMLineItem().Actual);
		},

		/**
		 * Check if ProductColors is disabled
		 */
		_isDisableProductColors: function() {
			var product = this._getProduct();
			return !product || !product.ProductColors.length;
		},

		/**
		 * Return if selecting ColorSpec is allowed on this bom line, i.e. PartMaterial.OKColorSpecs exist and is true
		 */
		_isOKColorSpecs: function() {
			var partMaterial = this._getBOMLineItem();
			return !partMaterial.ForStyle && !!partMaterial.OKColorSpecs;
		}
	});
});